// content.js merges two kinds of copy:
//  1. CMS-editable sections (imported from ./content/*.json — this is what
//     Sarah can change herself via the /changeprosecure admin console)
//  2. Structural/behavioral strings that stay hardcoded here on purpose —
//     nav anchors, form validation/UI microcopy, cookie-banner button
//     labels, footer link hrefs. Editing these could break the page
//     (dead anchors, broken form errors), so they're Pedro-only.
//
// App.js is unchanged — it still reads content[lang].hero.kicker etc.,
// exactly as before this file became a merge layer.

import hero from './content/hero.json';
import ticker from './content/ticker.json';
import help from './content/help.json';
import about from './content/about.json';
import approach from './content/approach.json';
import testimonials from './content/testimonials.json';
import telehealth from './content/telehealth.json';
import quote from './content/quote.json';
import cta from './content/cta.json';
import footer from './content/footer.json';
import cookieNotice from './content/cookieNotice.json';
import contactFormCopy from './content/contactFormCopy.json';
import legalPrivacy from './content/legal-privacy.json';
import legalTerms from './content/legal-terms.json';
import legalCookies from './content/legal-cookies.json';

// Structural/behavioral strings — not in the CMS, Pedro edits these directly.
const staticContent = {
  en: {
    nav: {
      links: [
        { label: "About", href: "#about" },
        { label: "Services", href: "#help" },
        { label: "Approach", href: "#approach" },
        { label: "Telehealth", href: "#telehealth" },
        { label: "Contact", href: "#contact" },
      ],
      contactBtn: "Contact Sarah",
    },
    footer: {
      navLinks: [["About", "#about"], ["Services", "#help"], ["Approach", "#approach"], ["Telehealth", "#telehealth"], ["Contact", "#contact"]],
      contactLines: ["240 Albert Road, South Melbourne VIC 3205", "+61 488 988 234", "Mon–Sat · Mornings · Late Afternoon · Evenings"],
      address: "240 Albert Road, South Melbourne VIC 3205",
      legalLinks: [["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Cookie Policy", "/cookies"]],
    },
    contactForm: {
      nameLabel: "Your Name",
      emailLabel: "Email Address",
      phoneLabel: "Phone (optional)",
      topicPlaceholder: "What brings you here?",
      topicOptions: ["Anxiety", "Trauma / EMDR", "Couples Counselling", "Adolescents", "Grief & Loss", "Other"],
      messagePlaceholder: "Your message...",
      preferredLanguageLabel: "Preferred language:",
      langOptions: [{ value: "English", label: "English" }, { value: "French", label: "French" }],
      genericError: "Something went wrong sending your message. Please try again in a moment.",
      checkErrorPrefix: "Please check:",
      turnstileMissing: "Please complete the verification check above before sending.",
      fieldRequired: "This field is required.",
      fieldInvalidEmail: "Please enter a valid email address.",
      sendingBtn: "Sending...",
      sendBtn: "Send Message",
      secureNote: "Sent securely. This goes straight to Sarah's inbox and isn't stored anywhere else.",
      infoAddress: "240 Albert Road, South Melbourne VIC 3205",
      infoAddressSub: "Near Clarendon St",
      infoPhone: "+61 488 988 234",
      infoHours: "Mon–Sat",
      infoHoursSub: "Mornings · Late Afternoon · Early Evening",
      telehealthNote: "Can't make it in? Telehealth available Australia-wide & internationally.",
      parkingNote: "Tram stop nearby · Street parking available",
      mapPrivacyNote: "To protect your privacy, this map only loads from Google when you choose to view it.",
      mapLoadLabel: "Load map",
    },
    cookieNotice: {
      privacyLinkLabel: "Privacy Policy",
      cookieLinkLabel: "Cookie Policy",
      accept: "Accept",
      decline: "Decline",
      reopenLabel: "Cookie preferences",
    },
  },
  fr: {
    nav: {
      links: [
        { label: "À propos", href: "#about" },
        { label: "Services", href: "#help" },
        { label: "Approche", href: "#approach" },
        { label: "Télésanté", href: "#telehealth" },
        { label: "Contact", href: "#contact" },
      ],
      contactBtn: "Contacter Sarah",
    },
    footer: {
      navLinks: [["À propos", "#about"], ["Services", "#help"], ["Approche", "#approach"], ["Télésanté", "#telehealth"], ["Contact", "#contact"]],
      contactLines: ["240 Albert Road, South Melbourne VIC 3205", "+61 488 988 234", "Lun–Sam · Matinées · Fin d'après-midi · Soirées"],
      address: "240 Albert Road, South Melbourne VIC 3205",
      legalLinks: [["Politique de confidentialité", "/privacy"], ["Conditions d'utilisation", "/terms"], ["Politique de cookies", "/cookies"]],
    },
    contactForm: {
      nameLabel: "Votre Nom",
      emailLabel: "Adresse E-mail",
      phoneLabel: "Téléphone (facultatif)",
      topicPlaceholder: "Qu'est-ce qui vous amène ?",
      topicOptions: ["Anxiété", "Traumatisme / EMDR", "Thérapie de Couple", "Adolescents", "Deuil", "Autre"],
      messagePlaceholder: "Votre message...",
      preferredLanguageLabel: "Langue préférée :",
      langOptions: [{ value: "English", label: "Anglais" }, { value: "French", label: "Français" }],
      genericError: "Une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer dans un instant.",
      checkErrorPrefix: "Veuillez vérifier :",
      turnstileMissing: "Veuillez compléter la vérification ci-dessus avant d'envoyer.",
      fieldRequired: "Ce champ est requis.",
      fieldInvalidEmail: "Veuillez saisir une adresse e-mail valide.",
      sendingBtn: "Envoi en cours...",
      sendBtn: "Envoyer le Message",
      secureNote: "Envoi sécurisé. Ce message va directement dans la boîte de réception de Sarah et n'est stocké nulle part ailleurs.",
      infoAddress: "240 Albert Road, South Melbourne VIC 3205",
      infoAddressSub: "Près de Clarendon St",
      infoPhone: "+61 488 988 234",
      infoHours: "Lun–Sam",
      infoHoursSub: "Matinées · Fin d'après-midi · Début de soirée",
      telehealthNote: "Vous ne pouvez pas vous déplacer ? La télésanté est disponible dans toute l'Australie et à l'international.",
      parkingNote: "Arrêt de tram à proximité · Stationnement dans la rue disponible",
      mapPrivacyNote: "Pour protéger votre vie privée, cette carte ne se charge depuis Google que si vous choisissez de l'afficher.",
      mapLoadLabel: "Afficher la carte",
    },
    cookieNotice: {
      privacyLinkLabel: "Politique de confidentialité",
      cookieLinkLabel: "Politique de cookies",
      accept: "Accepter",
      decline: "Refuser",
      reopenLabel: "Préférences de cookies",
    },
  },
};

function buildLang(lang) {
  return {
    nav: staticContent[lang].nav,
    hero: { ...hero[lang], heroImage: hero.heroImage },
    ticker: ticker[lang].items,
    howIHelp: help[lang],
    about: { ...about[lang], aboutImage: about.aboutImage },
    approach: approach[lang],
    testimonials: testimonials[lang],
    telehealth: telehealth[lang],
    quote: quote[lang],
    cta: cta[lang],
    contactForm: { ...staticContent[lang].contactForm, ...contactFormCopy[lang] },
    footer: { ...staticContent[lang].footer, ...footer[lang] },
    cookieNotice: { ...staticContent[lang].cookieNotice, ...cookieNotice[lang] },
    // Each doc's own "updated" date is nested inside it, not shared -- the
    // CMS gives Sarah an independent "Last updated" field per document
    // (config.yml's legal_fields), so each page needs to actually read
    // its own value instead of always showing Privacy's date (a real bug
    // caught by /code-review: config.yml promised per-doc dates that
    // content.js never actually wired up).
    legal: {
      privacy: { title: legalPrivacy[lang].title, updated: legalPrivacy[lang].updated, sections: legalPrivacy[lang].sections },
      terms: { title: legalTerms[lang].title, updated: legalTerms[lang].updated, sections: legalTerms[lang].sections },
      cookies: { title: legalCookies[lang].title, updated: legalCookies[lang].updated, sections: legalCookies[lang].sections },
    },
  };
}

export const content = {
  en: buildLang('en'),
  fr: buildLang('fr'),
};
